/**
 * Instructor Dashboard
 * Instructors and admins can access
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClasses } from '../hooks/useClasses';
import { useLabs } from '../hooks/useLabs';
import { Button } from '../components/ui/button';
import { QuickStatsCards } from '../components/instructor/QuickStatsCards';
import { ClassCard } from '../components/instructor/ClassCard';
import { ClassListFilters } from '../components/instructor/ClassListFilters';
import { RefreshControls } from '../components/instructor/RefreshControls';
import { CreateClassDialog, type CreateClassData } from '../components/instructor/CreateClassDialog';
import type { ClassStatus } from '@cloak-next/shared';

export function InstructorDashboard() {
  const navigate = useNavigate();
  const { classes, loading, error, fetchClasses, createClass } = useClasses();
  const { terminateLabs } = useLabs();

  const [filter, setFilter] = useState<ClassStatus | 'all'>('all');
  const [isPolling, setIsPolling] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Fetch classes on mount
  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // Auto-polling logic (5 minutes)
  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(() => {
      fetchClasses();
      setLastRefreshed(new Date());
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [isPolling, fetchClasses]);

  // Manual refresh handler
  const handleRefresh = async () => {
    await fetchClasses();
    setLastRefreshed(new Date());
  };

  // Filter classes based on selected tab
  const filteredClasses = useMemo(() => {
    if (!classes) return [];
    if (filter === 'all') return classes;
    return classes.filter((c) => c.status === filter);
  }, [classes, filter]);

  // Calculate class counts for filter badges
  const classCounts = useMemo(() => {
    if (!classes) {
      return { all: 0, draft: 0, scheduled: 0, active: 0, ended: 0 };
    }
    return {
      all: classes.length,
      draft: classes.filter((c) => c.status === 'draft').length,
      scheduled: classes.filter((c) => c.status === 'scheduled').length,
      active: classes.filter((c) => c.status === 'active').length,
      ended: classes.filter((c) => c.status === 'ended').length,
    };
  }, [classes]);

  // Calculate quick stats
  const stats = useMemo(() => {
    if (!classes) {
      return { activeClasses: 0, totalStudents: 0, provisionedClasses: 0 };
    }

    const activeClasses = classes.filter(
      (c) => c.status === 'active' || c.status === 'scheduled'
    ).length;

    const totalStudents = classes.reduce((sum, c) => sum + c.currentStudents, 0);

    const provisionedClasses = classes.filter((c) => c.labsProvisioned).length;

    return {
      activeClasses,
      totalStudents,
      provisionedClasses,
    };
  }, [classes]);

  // Handle provision labs - navigate to details page for full provisioning UI
  const handleProvisionLabs = (className: string) => {
    navigate(`/classes/${encodeURIComponent(className)}`);
  };

  // Handle terminate labs
  const handleTerminateLabs = async (className: string) => {
    try {
      await terminateLabs(className);
      // Refresh to get updated status
      await fetchClasses();
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Failed to terminate labs:', err);
      // TODO: Show error toast
    }
  };

  // Handle view details (navigate to class details page)
  const handleViewDetails = (className: string) => {
    navigate(`/classes/${encodeURIComponent(className)}`);
  };

  // Handle create class
  const handleCreateClass = async (data: CreateClassData) => {
    await createClass(data);
    // Refresh to show new class
    await fetchClasses();
    setLastRefreshed(new Date());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold">Manage Classes</h2>
          <p className="text-muted-foreground mt-2">
            Create and manage your training classes
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          Create Class
        </Button>
      </div>

      {/* Quick Stats */}
      <QuickStatsCards
        activeClasses={stats.activeClasses}
        totalStudents={stats.totalStudents}
        provisionedClasses={stats.provisionedClasses}
      />

      {/* Filters */}
      <ClassListFilters
        activeFilter={filter}
        onFilterChange={setFilter}
        classCounts={classCounts}
      />

      {/* Refresh Controls */}
      <RefreshControls
        onRefresh={handleRefresh}
        isRefreshing={loading}
        isPolling={isPolling}
        onPollingChange={setIsPolling}
        lastRefreshed={lastRefreshed}
      />

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          <p className="font-semibold">Error loading classes</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (!classes || classes.length === 0) && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading classes...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && (!classes || classes.length === 0) && (
        <div className="border rounded-lg p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">No classes yet</h3>
          <p className="text-muted-foreground mb-4">
            Get started by creating your first training class
          </p>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            Create Your First Class
          </Button>
        </div>
      )}

      {/* Class List */}
      {!loading && filteredClasses.length > 0 && (
        <div className="space-y-4">
          {filteredClasses.map((classData) => (
            <ClassCard
              key={classData.className}
              classData={classData}
              onViewDetails={handleViewDetails}
              onProvisionLabs={handleProvisionLabs}
              onTerminateLabs={handleTerminateLabs}
            />
          ))}
        </div>
      )}

      {/* Filtered Empty State */}
      {!loading && classes && classes.length > 0 && filteredClasses.length === 0 && (
        <div className="border rounded-lg p-8 text-center">
          <p className="text-muted-foreground">
            No classes with status "{filter}"
          </p>
        </div>
      )}

      {/* Create Class Dialog */}
      <CreateClassDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreateClass={handleCreateClass}
        existingClasses={classes || []}
      />
    </div>
  );
}
